// Modal for importing HTTP API configurations from JSON (WP-13).
import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { importHttpApi } from '../../lib/httpApi';

interface HttpApiImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export function HttpApiImportModal({ open, onClose, onImported }: HttpApiImportModalProps) {
  const { t } = useI18n();

  const [jsonText, setJsonText] = useState('');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setJsonText(String(event.target?.result || ''));
    };
    reader.readAsText(file);
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jsonText.trim()) {
      setError(t('httpApi.import.emptyError'));
      return;
    }

    setImporting(true);
    setError(null);

    try {
      let config = JSON.parse(jsonText.trim());
      if (config.exportConfig) {
        config = config.exportConfig;
      }
      if (!config.name || !config.method || !config.url) {
        throw new Error(t('httpApi.import.invalidStructureError'));
      }

      await importHttpApi(config);
      onImported();
      onClose();
    } catch (err: any) {
      setError(err.message || t('httpApi.import.failedError'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('httpApi.import.modalTitle')}
    >
      <form onSubmit={handleImport} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            {t('httpApi.import.jsonConfig')}
          </label>
          <label className="text-xs text-primary font-medium cursor-pointer hover:underline">
            <span>{t('httpApi.import.uploadFile')}</span>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
        </div>

        <textarea
          rows={8}
          required
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          placeholder={'{\n  "name": "My API",\n  "method": "POST",\n  "url": "https://api.example.com",\n  ...\n}'}
          className="w-full font-mono text-xs rounded-lg border border-slate-200 bg-slate-50 p-2.5 outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-900"
        />

        <p className="text-[11px] text-slate-400">
          {t('httpApi.import.hint')}
        </p>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-800">
          <Button variant="ghost" type="button" onClick={onClose} disabled={importing}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={importing}>
            {importing ? t('common.loading') : t('httpApi.import.importButton')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
